import { css } from '@emotion/react';

import { breakpoints } from '@coursera/cds-common';

import { getPaletteColor } from './colors';
import type {
  DialogColor,
  DialogVariant,
  SlotOrientation,
  DialogWidth,
} from './types';

const getVariants = (isSmall: boolean) => {
  const spacing = isSmall ? 'var(--cds-spacing-200)' : 'var(--cds-spacing-300)';

  return {
    standard: css`
      padding-top: ${spacing};
      padding-inline-end: ${spacing};
    `,

    transactional: css`
      padding: 0;
    `,

    sticky: css`
      position: sticky;
      top: 0;
    `,
  };
};

/**
 * Determines if the close button should ALWAYS be absolutely positioned.
 * This is needed when a slot occupies the top-right area of the dialog:
 * - verticalTop: slot is on top, close floats over it
 * - horizontalEnd: slot is on right, close floats on top-right
 */
export const isCloseAlwaysAbsolute = (
  slotOrientation?: SlotOrientation
): boolean => {
  return (
    slotOrientation === 'verticalTop' || slotOrientation === 'horizontalEnd'
  );
};

/**
 * Determines if the close button should be absolutely positioned on mobile only.
 * This is for horizontalStart slots - on mobile the slot moves to top,
 * so the close button needs to float over it.
 */
export const isCloseAbsoluteOnMobile = (
  slotOrientation?: SlotOrientation
): boolean => {
  return (
    isCloseAlwaysAbsolute(slotOrientation) ||
    slotOrientation === 'horizontalStart'
  );
};

// Shared absolute positioning styles
const absoluteStyles = css`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-start;
  padding-top: var(--cds-spacing-200);
  padding-inline-end: var(--cds-spacing-200);
  background-color: unset;
`;

export const getCloseCss = ({
  color,
  variant,
  width,
  sticky,
  isAbsolute = false,
  absoluteOnMobile = false,
}: {
  color: DialogColor;
  variant: DialogVariant;
  width: DialogWidth;
  sticky: boolean;
  isAbsolute?: boolean;
  absoluteOnMobile?: boolean;
}) => {
  // Always absolute (prefix vertical, suffix horizontal)
  if (isAbsolute) {
    return absoluteStyles;
  }

  // Grid-based positioning with optional responsive absolute on mobile
  const isSmall = width === 'small';
  const variants = getVariants(isSmall);

  return css`
    grid-area: close;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: flex-start;
    background-color: ${getPaletteColor(color)};
    z-index: 1;

    ${breakpoints.down('xs')} {
      border-radius: 0;
      ${absoluteOnMobile && absoluteStyles}
    }

    ${variants[variant]}

    ${sticky && variants.sticky}
  `;
};

// White circular background styles for close button (24px circle)
const whiteCircleBackground = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--cds-color-neutral-background-primary);
`;

/**
 * Returns CSS for the white circular background behind the close button.
 * - When isAbsolute: always show white background
 * - When absoluteOnMobile: show white background only on mobile (xs breakpoint)
 */
export const getCloseButtonBackgroundCss = ({
  isAbsolute = false,
  absoluteOnMobile = false,
}: {
  isAbsolute?: boolean;
  absoluteOnMobile?: boolean;
}) => {
  // Always show white background (prefix vertical, suffix horizontal)
  if (isAbsolute) {
    return whiteCircleBackground;
  }

  // Show white background only on mobile (prefix horizontal)
  if (absoluteOnMobile) {
    return css`
      display: flex;
      align-items: center;
      justify-content: center;

      ${breakpoints.down('xs')} {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background-color: var(--cds-color-neutral-background-primary);
      }
    `;
  }

  // No background needed
  return css`
    display: flex;
    align-items: center;
    justify-content: center;
  `;
};
